import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, ButtonBase, Stack, Tooltip, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { EMPTY_VALUE_PLACEHOLDER, formatShortWeekdayDate } from "@shared";

import { DutyScheduleCell } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/data-entry/DutyScheduleDataEntry.utils";
import { useTranslation } from "@/translations";

interface Props {
  // undefined when no such cell exists — a court column on a day that court did not sit.
  cell: DutyScheduleCell | undefined;
  date: Date;
  dutyLabel: string;
  isEditable: boolean;
  isHighlighted: boolean;
  isOpen: boolean;
  onOpen: (anchor: HTMLElement) => void;
}

// A cell the duty schedule never had stays shaded and inert; there is no duty there to staff. Every
// other cell opens the selection overlay, including one nobody is on.
export function DutyScheduleAssignmentCell({
  cell,
  date,
  dutyLabel,
  isEditable,
  isHighlighted,
  isOpen,
  onOpen,
}: Props) {
  const { t } = useTranslation();

  if (cell == null) {
    return (
      <Box component="span" sx={visuallyHidden}>
        {t("dutySchedule.detail.dataEntry.notInSession")}
      </Box>
    );
  }

  const occupant = cell.userCode ?? t("dutySchedule.detail.dataEntry.cellUnoccupied");
  const cellLabel = t("dutySchedule.detail.dataEntry.cellAria", {
    duty: dutyLabel,
    date: formatShortWeekdayDate(date),
    occupant,
  });
  const warningLabel = t("dutySchedule.detail.dataEntry.notPreferred");
  const showWarning = cell.userCode != null && !cell.matchesPreference;

  const content = (
    <Stack direction="row" alignItems="center" gap={0.25} minWidth={0} flex={1} justifyContent={"space-between"}>
      {cell.userCode == null ? (
        <Typography component="span" variant="body2" color="text.disabled">
          {EMPTY_VALUE_PLACEHOLDER}
        </Typography>
      ) : (
        <Typography component="span" variant="body2" noWrap>
          {cell.userCode}
        </Typography>
      )}
      {showWarning && (
        <Tooltip title={warningLabel}>
          <Box display="flex" color="warning.dark">
            <WarningAmberRoundedIcon sx={{ fontSize: "1rem" }} />
            <Box component="span" sx={visuallyHidden}>
              {warningLabel}
            </Box>
          </Box>
        </Tooltip>
      )}
    </Stack>
  );

  const highlightedLabel = isHighlighted && (
    <Box component="span" sx={visuallyHidden}>
      {t("dutySchedule.detail.dataEntry.filter.highlighted")}
    </Box>
  );

  if (!isEditable) {
    return (
      <>
        {content}
        {highlightedLabel}
        {cell.userCode == null && (
          <Box component="span" sx={visuallyHidden}>
            {t("dutySchedule.detail.dataEntry.unfilled")}
          </Box>
        )}
      </>
    );
  }

  return (
    <ButtonBase
      onClick={(event) => onOpen(event.currentTarget)}
      aria-label={cellLabel}
      // "dialog", not "listbox": what opens is a search field plus a list of buttons, and
      // promising option semantics a screen reader then cannot find is worse than none.
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      sx={{
        width: "100%",
        // WCAG 2.2 target size: the cell is short, so the hit area has to make it up.
        minHeight: 24,
        px: 1,
        py: 0.75,
        justifyContent: "flex-start",
        "&:focus-visible": { outline: 2, outlineColor: "primary.main", outlineOffset: -2 },
      }}
    >
      {content}
      {highlightedLabel}
      <Box display="flex" color="text.secondary" ml="auto" aria-hidden>
        <UnfoldMoreRoundedIcon sx={{ fontSize: "0.875rem" }} />
      </Box>
    </ButtonBase>
  );
}
